/**
 * The signed-in user's own profile.
 *
 * Separate from adminService rather than folded into it: adminService's
 * handleResponse maps every 403 to "Access denied - can_view_admin_dash
 * permission required" (adminService.js:39-41), which is the wrong sentence for
 * an endpoint that has no such gate. GET /user answers for whoever holds the
 * token and needs no permission at all.
 *
 * This is also the first /api/user call site to route its 401 through
 * sessionExpiry. The four existing inline callers each hand-roll one, and they
 * disagree: Dashboard.js:36-41 clears four localStorage keys where
 * SESSION_STORAGE_KEYS has five, stranding userPermissions.
 */

import { API_BASE_URL, getAuthHeaders } from './api';
import { raiseSessionExpired } from './sessionExpiry';
import { readBody, messageFrom, sendRequest, readSuccessBody } from './httpResponse';

/**
 * Fetch the current user (common.UserDTO).
 *
 * Note the DTO is not the same shape as the admin detail response — it answers
 * `activeSubscription` / `subscriptionExpiration` in camelCase where the admin
 * endpoint uses snake_case, and `roles` is a plain string array with no grant
 * provenance. Callers adapt at their own boundary; see Account/index.js.
 */
export const getCurrentUser = async () => {
  // sendRequest, not fetch: this is the Security tab's first call, so a browser
  // that is simply offline would otherwise open the tab on an Alert reading
  // "Failed to fetch". Only the transport is inside it — the 401 below still
  // raises SessionExpiredError, and callers still dispatch on that.
  const response = await sendRequest(`${API_BASE_URL}/user`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      raiseSessionExpired();
    }

    const body = await readBody(response);
    throw new Error(messageFrom(body, `Could not load your account (HTTP ${response.status}).`));
  }

  return readSuccessBody(response);
};
